if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      abortController = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];
      purchaseQuantityChangeHandler = undefined;
      swapUtilityInitialized = false;

      constructor() {
        super();
        this.quantityInput = this.querySelector('.quantity__input');
      }

      connectedCallback() {
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.initQuantityHandlers();
        this.initPurchaseQuantityPolish();
        this.dispatchEvent(new CustomEvent('product-info:loaded', { bubbles: true }));
      }

      disconnectedCallback() {
        this.abortController?.abort?.();
        this.onVariantChangeUnsubscriber?.();
        this.cartUpdateUnsubscriber?.();
        if (this.quantityInput && this.purchaseQuantityChangeHandler) {
          this.quantityInput.removeEventListener('change', this.purchaseQuantityChangeHandler);
        }
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      initializeProductSwapUtility() {
        if (this.swapUtilityInitialized) return;
        this.swapUtilityInitialized = true;

        this.preProcessHtmlCallbacks.push((html) =>
          html.querySelectorAll('.scroll-trigger').forEach((element) => element.classList.add('scroll-trigger--cancel'))
        );
        this.postProcessHtmlCallbacks.push(() => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.quantityForm = this.querySelector('.product-form__quantity');
        if (!this.quantityForm) return;

        this.setQuantityBoundries();

        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.fetchQuantityRules.bind(this));
        }
      }

      initPurchaseQuantityPolish() {
        if (!this.quantityInput) return;
        if (!this.classList.contains('cbh-quickadd') && !this.hasAttribute('data-cbh-main-product')) return;

        this.purchaseQuantityChangeHandler = () => {
          this.productForm?.handleErrorMessage();
          this.syncPurchaseQuantityControls({ clampValue: true });
        };

        this.quantityInput.addEventListener('change', this.purchaseQuantityChangeHandler);
        this.syncPurchaseQuantityControls({ clampValue: true });
      }

      syncPurchaseQuantityControls({ clampValue = false } = {}) {
        if (!this.quantityInput) return;
        if (!this.classList.contains('cbh-quickadd') && !this.hasAttribute('data-cbh-main-product')) return;

        const input = this.quantityInput;
        const quantity = input.closest('quantity-input');
        const minus = quantity?.querySelector('.quantity__button[name="minus"]');
        const plus = quantity?.querySelector('.quantity__button[name="plus"]');
        const ruleMin = Number.parseInt(input.dataset.min || '1', 10) || 1;
        const cartQuantity = Number.parseInt(input.dataset.cartQuantity || '0', 10) || 0;
        const min = input.min !== '' ? Number.parseInt(input.min, 10) : null;
        const max = input.max !== '' ? Number.parseInt(input.max, 10) : null;
        const step = Number.parseInt(input.step || '1', 10) || 1;
        const requiredAddition = cartQuantity >= ruleMin ? step : ruleMin;
        const limitSource = input.dataset.limitSource || '';
        let value = Number.parseInt(input.value, 10);

        const submit = this.productForm?.submitButton;
        const inventory = this.querySelector(`#Inventory-${this.dataset.section}`);
        const inventoryState = inventory?.dataset.state;
        const submitAlreadyUnavailable =
          !!submit?.hasAttribute('disabled') && submit?.dataset.cbhQuantityLimitBlocked !== 'true';
        const noValidQuantity =
          Number.isFinite(max) && max < requiredAddition && inventoryState !== 'backsoon' && !submitAlreadyUnavailable;

        // Inventory and Shopify quantity rules can produce a remaining allowance
        // smaller than the next valid order quantity. Do not expose an invalid
        // min/max combination or let the customer submit a quantity Shopify must
        // reject. A value of zero is intentional here: there is currently no valid
        // additional quantity for this variant.
        if (noValidQuantity) {
          value = 0;
          input.min = '0';
          input.value = '0';

          if (minus) {
            minus.disabled = true;
            minus.setAttribute('aria-disabled', 'true');
          }
          if (plus) {
            plus.disabled = true;
            plus.setAttribute('aria-disabled', 'true');
          }

          if (submit) {
            submit.dataset.cbhQuantityLimitBlocked = 'true';
            const buttonLabel =
              max <= 0 ? 'Maximum in basket' : limitSource === 'inventory' || limitSource === 'both'
                ? 'Not enough stock'
                : 'Basket limit reached';
            this.productForm.toggleSubmitButton(true, buttonLabel);
            this.announce(`${buttonLabel}.`);
          }
          return;
        }

        if (!Number.isFinite(value)) value = Number.isFinite(min) ? min : step;

        if (clampValue) {
          if (Number.isFinite(min)) value = Math.max(min, value);
          if (Number.isFinite(max)) value = Math.min(max, value);
          input.value = String(value);
        }

        if (minus) {
          const disabled = Number.isFinite(min) ? value <= min : false;
          minus.disabled = disabled;
          minus.setAttribute('aria-disabled', String(disabled));
        }

        if (plus) {
          const disabled = Number.isFinite(max) ? value + step > max : false;
          plus.disabled = disabled;
          plus.setAttribute('aria-disabled', String(disabled));
        }

        if (!submit) return;

        if (submit.dataset.cbhQuantityLimitBlocked === 'true') {
          delete submit.dataset.cbhQuantityLimitBlocked;
          const variantId = this.productForm?.variantIdInput?.value;
          if (variantId && inventoryState !== 'backsoon') this.productForm.toggleSubmitButton(false);
        }
      }

      // ---------- A11Y: live region for variant updates ----------
      getLiveRegion() {
        let el = this.querySelector('[data-product-live-region]');
        if (el) return el;

        el = document.createElement('p');
        el.className = 'visually-hidden';
        el.setAttribute('role', 'status');
        el.setAttribute('aria-live', 'polite');
        el.setAttribute('aria-atomic', 'true');
        el.setAttribute('data-product-live-region', 'true');

        // Put it near the top so SRs pick it up reliably
        this.prepend(el);
        return el;
      }

      announce(message) {
        try {
          if (!message) return;
          const el = this.getLiveRegion();
          // Clear then set (forces SR announcement in more browsers)
          el.textContent = '';
          requestAnimationFrame(() => {
            el.textContent = message;
          });
        } catch (e) {}
      }
      // ----------------------------------------------------------

      handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
        if (!this.contains(event.target)) return;

        this.resetProductFormState();

        const isQuickAdd = this.classList.contains('cbh-quickadd');
        const productUrl = isQuickAdd
          ? this.pendingRequestUrl || this.dataset.url
          : target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
        this.pendingRequestUrl = productUrl;

        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;

        const targetId = event?.target?.id || target?.id;

this.renderProductInfo({
  requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage),
  targetId,
  callback: shouldSwapProduct
    ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
    : this.handleUpdateProductInfo(productUrl),
});
      }

      resetProductFormState() {
        const productForm = this.productForm;
        productForm?.toggleSubmitButton(true);
        productForm?.handleErrorMessage();
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return (html) => {
          this.productModal?.remove();

          const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
          const variant = this.getSelectedVariant(html.querySelector(selector));
          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;

            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }
        };
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        this.abortController = new AbortController();

        fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => {
            if (!response.ok) throw new Error(`Variant update failed with status ${response.status}.`);
            return response.text();
          })
          .then((responseText) => {
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            callback(html);
          })
          .then(() => {
           if (targetId) document.getElementById(targetId)?.focus();
          })
          .catch((error) => {
            if (error?.name === 'AbortError') return;
            this.pendingRequestUrl = null;
            window.cbhLogError?.(error);

            // The inline form error is role=alert, so don't duplicate the same
            // failure through the polite product status region. Keep purchasing
            // disabled until a fresh server-rendered option state succeeds.
            const message = this.classList.contains('cbh-quickadd')
              ? 'We couldn’t update that option. Please choose another option or view the full product page.'
              : 'We couldn’t update that option. Please try again or refresh the page.';
            this.productForm?.handleErrorMessage(message);
          });
      }

      getSelectedVariant(productInfoNode) {
        if (!productInfoNode) return null;
        const selectedVariant = productInfoNode.querySelector(
          'variant-selects [data-selected-variant], variant-radios [data-selected-variant]'
        )?.innerHTML;
        return !!selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
  const params = [];

  const isQuickAddRequest = url.includes('view=quick-add');
  !shouldFetchFullPage && !isQuickAddRequest && params.push(`section_id=${this.sectionId}`);

  if (optionValues.length) {
    params.push(`option_values=${optionValues.join(',')}`);
  }

  if (!params.length) return url;
  const joiner = url.includes('?') ? '&' : '?';
  return `${url}${joiner}${params.join('&')}`;
}

      updateOptionValues(html) {
        const variantSelectors = html.querySelector('variant-selects, variant-radios');
        if (variantSelectors && this.variantSelectors) {
          HTMLUpdateUtility.viewTransition(this.variantSelectors, variantSelectors, this.preProcessHtmlCallbacks);
        }
      }

      handleUpdateProductInfo(productUrl) {
        return (html) => {
          const variant = this.getSelectedVariant(html);

          this.pickupAvailability?.update(variant);
          this.updateOptionValues(html);
          this.updateURL(productUrl, variant?.id);
          this.updateVariantInputs(variant?.id);

          if (!variant) {
            this.setUnavailable();
            this.announce('This variant is unavailable.');
            return;
          }

          this.updateMedia(html, variant?.featured_media?.id);

          const updateSourceFromDestination = (id, shouldHide = () => false) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(`#${id}-${this.dataset.section}`);
            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle('hidden', shouldHide(source));

              if (id === 'Inventory') {
                if (source.dataset.state) destination.dataset.state = source.dataset.state;
                else destination.removeAttribute('data-state');
                destination.classList.toggle('visibility-hidden', source.classList.contains('visibility-hidden'));
              }
            }
          };

          updateSourceFromDestination('price');
          updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Inventory', ({ innerText }) => innerText === '');
          updateSourceFromDestination('Volume');
          updateSourceFromDestination('Price-Per-Item', ({ classList }) => classList.contains('hidden'));

          this.updateQuantityRules(this.sectionId, html);
          this.querySelector(`#Quantity-Rules-${this.dataset.section}`)?.classList.remove('hidden');
          this.querySelector(`#Volume-Note-${this.dataset.section}`)?.classList.remove('hidden');

          // In quick-add / minimal views the submit button ID might not exist in the fetched HTML.
          // Fall back to variant availability instead of defaulting to disabled (which can "stick" Sold out).
          const submitButton = html.getElementById(`ProductSubmitButton-${this.sectionId}`);
          const isDisabled = submitButton ? submitButton.hasAttribute('disabled') : !variant?.available;
          const fetchedSubmitLabel = submitButton
            ?.querySelector('.cbh-atc__label')
            ?.textContent
            ?.replace(/\s+/g, ' ')
            ?.trim();

          if (this.productForm?.submitButton) {
            if (submitButton?.dataset.cbhQuantityLimitBlocked === 'true') {
              this.productForm.submitButton.dataset.cbhQuantityLimitBlocked = 'true';
            } else {
              delete this.productForm.submitButton.dataset.cbhQuantityLimitBlocked;
            }
          }

          this.productForm?.toggleSubmitButton(
            isDisabled,
            isDisabled
              ? fetchedSubmitLabel || (window.variantStrings && window.variantStrings.soldOut) || 'Sold out'
              : undefined
          );
          this.syncPurchaseQuantityControls({ clampValue: true });

          // A11Y: announce a clean summary
          try {
            const priceText = this.querySelector(`#price-${this.dataset.section}`)?.textContent?.trim() || '';
            const invText = this.querySelector(`#Inventory-${this.dataset.section}`)?.textContent?.trim() || '';
            const disabledState = fetchedSubmitLabel || 'Unavailable';
            const availability = isDisabled ? `${disabledState}.` : 'Available.';
            const parts = [priceText, invText, availability].filter(Boolean);
            this.announce(parts.join(' '));
          } catch (e) {}

          publish(PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });
        };
      }

      updateVariantInputs(variantId) {
        this.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
        ).forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          if (!input) return;
          input.value = variantId ?? '';
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }

      updateURL(url, variantId) {
        // Share is auxiliary UI. Never allow an unavailable/uninitialised share
        // controller to break the core variant purchase journey.
        this.querySelector('share-button')?.updateUrl?.(
          `${window.shopUrl}${url}${variantId ? `?variant=${variantId}` : ''}`
        );

        if (this.dataset.updateUrl === 'false') return;
        window.history.replaceState({}, '', `${url}${variantId ? `?variant=${variantId}` : ''}`);
      }

      setUnavailable() {
       this.productForm?.toggleSubmitButton(
  true,
  (window.variantStrings && window.variantStrings.unavailable) || 'Unavailable'
);

        const selectors = ['price', 'Inventory', 'Sku', 'Price-Per-Item', 'Volume-Note', 'Volume', 'Quantity-Rules']
          .map((id) => `#${id}-${this.dataset.section}`)
          .join(', ');
        this.querySelectorAll(selectors).forEach(({ classList }) => classList.add('hidden'));
      }

      updateMedia(html, variantFeaturedMediaId) {
        // Quick add (Mini PDP) view: swap the simple media block instead of media-gallery
        const qaSource = this.querySelector('.cbh-quickadd__media');
        const qaDest = html.querySelector('.cbh-quickadd__media');
        if (qaSource && qaDest) {
          qaSource.innerHTML = qaDest.innerHTML;
          return;
        }

        if (!variantFeaturedMediaId) return;

        const mediaGallerySource = this.querySelector('media-gallery ul');
        const mediaGalleryDestination = html.querySelector(`media-gallery ul`);

        const refreshSourceData = () => {
          if (this.hasAttribute('data-zoom-on-hover')) enableZoomOnHover(2);
          const mediaGallerySourceItems = Array.from(mediaGallerySource.querySelectorAll('li[data-media-id]'));
          const sourceSet = new Set(mediaGallerySourceItems.map((item) => item.dataset.mediaId));
          const sourceMap = new Map(
            mediaGallerySourceItems.map((item, index) => [item.dataset.mediaId, { item, index }])
          );
          return [mediaGallerySourceItems, sourceSet, sourceMap];
        };

        if (mediaGallerySource && mediaGalleryDestination) {
          let [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
          const mediaGalleryDestinationItems = Array.from(
            mediaGalleryDestination.querySelectorAll('li[data-media-id]')
          );
          const destinationSet = new Set(mediaGalleryDestinationItems.map(({ dataset }) => dataset.mediaId));
          let shouldRefresh = false;

          for (let i = mediaGalleryDestinationItems.length - 1; i >= 0; i--) {
            if (!sourceSet.has(mediaGalleryDestinationItems[i].dataset.mediaId)) {
              mediaGallerySource.prepend(mediaGalleryDestinationItems[i]);
              shouldRefresh = true;
            }
          }

          for (let i = 0; i < mediaGallerySourceItems.length; i++) {
            if (!destinationSet.has(mediaGallerySourceItems[i].dataset.mediaId)) {
              mediaGallerySourceItems[i].remove();
              shouldRefresh = true;
            }
          }

          if (shouldRefresh) [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();

          mediaGalleryDestinationItems.forEach((destinationItem, destinationIndex) => {
            const sourceData = sourceMap.get(destinationItem.dataset.mediaId);

            if (sourceData && sourceData.index !== destinationIndex) {
              mediaGallerySource.insertBefore(
                sourceData.item,
                mediaGallerySource.querySelector(`li:nth-of-type(${destinationIndex + 1})`)
              );

              [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
            }
          });
        }

        this.querySelector(`media-gallery`)?.setActiveMedia?.(
          `${this.dataset.section}-${variantFeaturedMediaId}`,
          true
        );

        const modalContent = this.productModal?.querySelector(`.product-media-modal__content`);
        const newModalContent = html.querySelector(`product-modal .product-media-modal__content`);
        if (modalContent && newModalContent) modalContent.innerHTML = newModalContent.innerHTML;
      }

      setQuantityBoundries() {
        const data = {
          cartQuantity: this.quantityInput.dataset.cartQuantity ? parseInt(this.quantityInput.dataset.cartQuantity) : 0,
          min: this.quantityInput.dataset.min ? parseInt(this.quantityInput.dataset.min) : 1,
          max: this.quantityInput.dataset.max ? parseInt(this.quantityInput.dataset.max) : null,
          step: this.quantityInput.step ? parseInt(this.quantityInput.step) : 1,
        };

        const max = data.max === null ? data.max : Math.max(0, data.max - data.cartQuantity);
        const isCbhPurchaseSurface = this.classList.contains('cbh-quickadd') || this.hasAttribute('data-cbh-main-product');
        let min = data.min;

        if (isCbhPurchaseSurface) {
          const nextValidQuantity = data.cartQuantity >= data.min ? data.step : data.min;
          min = max !== null && max < nextValidQuantity ? 0 : nextValidQuantity;
        } else {
          if (max !== null) min = Math.min(min, max);
          if (data.cartQuantity >= data.min) min = Math.min(min, data.step);
        }

        this.quantityInput.min = min;

        if (max !== null) {
          this.quantityInput.max = max;
        } else {
          this.quantityInput.removeAttribute('max');
        }
        this.quantityInput.value = min;

        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
        this.syncPurchaseQuantityControls({ clampValue: true });
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.variantIdInput?.value;
        if (!currentVariantId) return;

        const spinner = this.querySelector('.quantity__rules-cart .loading__spinner');
        if (!spinner) return;

        spinner.classList.remove('hidden');

        let quantityRulesUrl;
        try {
          const url = new URL(this.dataset.url, window.location.origin);
          url.searchParams.set('variant', currentVariantId);
          if (!url.searchParams.has('view')) url.searchParams.set('section_id', this.dataset.section);
          quantityRulesUrl = url.toString();
        } catch (error) {
          const joiner = this.dataset.url.includes('?') ? '&' : '?';
          quantityRulesUrl = `${this.dataset.url}${joiner}variant=${currentVariantId}`;
        }

        fetch(quantityRulesUrl)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.dataset.section, html);
          })
          .catch((e) => window.cbhLogError?.(e))
          .finally(() => spinner.classList.add('hidden'));
      }

      updateQuantityRules(sectionId, html) {
        if (!this.quantityInput) return;

        const quantityFormUpdated = html.getElementById(`Quantity-Form-${sectionId}`);
        if (!quantityFormUpdated || !this.quantityForm) {
          this.setQuantityBoundries();
          return;
        }

        const selectors = ['.quantity__input', '.quantity__rules', '.quantity__label'];
        for (let selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);
          if (!current || !updated) continue;

          if (selector === '.quantity__input') {
            const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'data-limit-source', 'step', 'aria-label'];
            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) {
                current.setAttribute(attribute, valueUpdated);
              } else {
                current.removeAttribute(attribute);
              }
            }
          } else {
            current.innerHTML = updated.innerHTML;
          }
        }

        // Apply boundaries only after the newly selected variant's rules have
        // replaced the previous variant's attributes.
        this.setQuantityBoundries();
      }

      get productForm() {
        return this.querySelector(`product-form`);
      }

      get productModal() {
        return document.querySelector(`#ProductModal-${this.dataset.section}`);
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get variantSelectors() {
        return this.querySelector('variant-selects, variant-radios');
      }

      get relatedProducts() {
        const relatedProductsSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'related-products'
        );
        return document.querySelector(`product-recommendations[data-section-id^="${relatedProductsSectionId}"]`);
      }

      get quickOrderList() {
        const quickOrderListSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'quick_order_list'
        );
        return document.querySelector(`quick-order-list[data-id^="${quickOrderListSectionId}"]`);
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }
    }
  );
}
